/**
 * ScorecardView — renders a batch's scorecard, the union of two shapes
 * (`Batch.scorecard: Record<string, unknown> | null` in
 * src/types/factory.ts is intentionally free-form — the backend contract
 * is mode-dependent, see PLANS/specs/2026-07-03-annotation-factory-design.md
 * §scorecard):
 *
 *   GT mode   (`mode: "gt"`)   — a demo batch scored against the project's
 *             golden-GT annotator. `status: "ok"` carries `overall_agreement`
 *             (0..1) and a top-level `field_agreements` map (field name →
 *             0..1). `status: "no_ground_truth"` means the project has no
 *             `gt_annotator` configured yet — there is nothing to score
 *             against, and no `overall_agreement` field will ever appear.
 *   Peer mode (`mode: "peer"`) — a working-phase batch scored against a
 *             randomly-paired second labeler on the overlap slice.
 *             `overall_agreement` + one row per `per_case[]` entry, each
 *             with its own `counterpart` annotator and a NESTED
 *             `field_agreements` map. `pending_peer[]` lists case uids
 *             still waiting on a second labeler to finish. `status:
 *             "pending_peer"` (or an empty/missing `per_case`) means no
 *             peer overlap has resolved yet.
 *
 * CRITICAL (feedback_no_silent_fallbacks): every branch below that lacks a
 * real number renders an explicit message or "—" — NEVER a fabricated
 * `0.00`. A missing/malformed field is treated exactly like an absent one,
 * not coerced to zero. Mask Dice/IoU has no backend field yet (Stage C) —
 * `MaskMetricsRow` only renders if the corresponding key is actually present
 * and numeric in the dict, so wiring it up later is additive (no rewrite of
 * this "never fake a 0" contract).
 */

import React from "react";

export interface ScorecardViewProps {
  scorecard: Record<string, unknown> | null | undefined;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null;
}

function fieldAgreementEntries(value: unknown): Array<[string, number]> {
  const record = asRecord(value);
  if (!record) return [];
  return Object.entries(record)
    .map(([field, v]) => [field, asNumber(v)] as const)
    .filter((entry): entry is [string, number] => entry[1] !== null);
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded border border-border/50 bg-surface p-6 text-center text-sm text-fg-muted">
      {children}
    </div>
  );
}

function OverallAgreement({ value, label }: { value: number; label: string }) {
  const low = value < 0.8;
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-fg-dim">
        {label}
      </span>
      <span className="block h-1.5 w-[110px] overflow-hidden rounded-sharp bg-white/[0.06]">
        <span
          className={`block h-full rounded-sharp ${low ? "bg-warn" : "bg-primary"}`}
          style={{ width: `${Math.round(value * 100)}%` }}
        />
      </span>
      <span className={`font-mono text-sm font-medium ${low ? "text-warn" : "text-fg"}`}>
        {value.toFixed(2)}
      </span>
    </div>
  );
}

function FieldAgreementBars({ entries }: { entries: Array<[string, number]> }) {
  if (entries.length === 0) {
    return <div className="text-xs text-fg-faint">No per-field breakdown available — —</div>;
  }
  return (
    <div className="flex flex-col gap-2">
      {entries.map(([field, value]) => {
        const low = value < 0.8;
        return (
          <div key={field} className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-xs text-fg-muted">{field}</span>
            <span className="block h-1 w-[90px] overflow-hidden rounded-sharp bg-white/[0.06]">
              <span
                className={`block h-full rounded-sharp ${low ? "bg-warn" : "bg-primary"}`}
                style={{ width: `${Math.round(value * 100)}%` }}
              />
            </span>
            <span className={`font-mono text-[11.5px] ${low ? "text-warn" : "text-fg-muted"}`}>
              {value.toFixed(2)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Mask Dice/IoU — Stage C, no backend field yet. Only ever renders when the
 *  key is present and numeric; otherwise contributes nothing (not even a
 *  "—" row), since the metric doesn't exist for this project/mode today. */
function MaskMetricsRow({ record }: { record: Record<string, unknown> | null }) {
  const dice = asNumber(record?.mask_dice);
  const iou = asNumber(record?.mask_iou);
  if (dice === null && iou === null) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-4 border-t border-border/50 pt-3 text-xs text-fg-muted">
      {dice !== null && (
        <span>
          Mask Dice <b className="font-mono text-fg">{dice.toFixed(2)}</b>
        </span>
      )}
      {iou !== null && (
        <span>
          Mask IoU <b className="font-mono text-fg">{iou.toFixed(2)}</b>
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GT mode
// ---------------------------------------------------------------------------

function GtScorecard({ scorecard }: { scorecard: Record<string, unknown> }) {
  const status = asString(scorecard.status);

  if (status === "no_ground_truth") {
    return (
      <EmptyNote>
        No ground truth configured for this project — set a golden-GT annotator to score demo
        batches.
      </EmptyNote>
    );
  }

  const overall = asNumber(scorecard.overall_agreement);
  const fields = fieldAgreementEntries(scorecard.field_agreements);

  if (overall === null) {
    // status !== "ok" and/or no number present — never guess a value.
    return <EmptyNote>Scorecard pending — no ground-truth agreement yet.</EmptyNote>;
  }

  return (
    <div className="flex flex-col gap-4">
      <OverallAgreement value={overall} label="vs golden GT" />
      <FieldAgreementBars entries={fields} />
      <MaskMetricsRow record={scorecard} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Peer mode
// ---------------------------------------------------------------------------

interface PeerCaseRow {
  caseUid: string | null;
  counterpart: string | null;
  agreement: number | null;
  fields: Array<[string, number]>;
}

function readPeerCase(value: unknown): PeerCaseRow {
  const record = asRecord(value) ?? {};
  return {
    caseUid: asString(record.case_uid),
    counterpart: asString(record.counterpart),
    agreement: asNumber(record.agreement),
    fields: fieldAgreementEntries(record.field_agreements),
  };
}

function PendingPeerList({ uids }: { uids: string[] }) {
  if (uids.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
      <span className="text-fg-dim">awaiting 2nd labeler:</span>
      {uids.map((uid) => (
        <span
          key={uid}
          className="rounded border border-border-strong bg-surface-2 px-2 py-0.5 font-mono text-[11px] text-fg-muted"
        >
          {uid}
        </span>
      ))}
    </div>
  );
}

function PeerScorecard({ scorecard }: { scorecard: Record<string, unknown> }) {
  const status = asString(scorecard.status);
  const perCase = (asArray(scorecard.per_case) ?? []).map(readPeerCase);
  const pendingPeerUids = (asArray(scorecard.pending_peer) ?? [])
    .map(asString)
    .filter((uid): uid is string => uid !== null);

  if (status === "pending_peer" || perCase.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <EmptyNote>Pending — waiting on peers to finish the overlap slice.</EmptyNote>
        <PendingPeerList uids={pendingPeerUids} />
      </div>
    );
  }

  const overall = asNumber(scorecard.overall_agreement);

  return (
    <div className="flex flex-col gap-4">
      {overall !== null ? (
        <OverallAgreement value={overall} label="peer IAA" />
      ) : (
        <div className="text-xs text-fg-faint">— (overall agreement not available)</div>
      )}

      <div className="flex flex-col divide-y divide-border/50 rounded border border-border/50">
        {perCase.map((row, i) => (
          <div key={row.caseUid ?? i} className="flex flex-col gap-2 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-fg-muted">
                {row.caseUid ?? `case ${i + 1}`}
              </span>
              <span className="text-[11px] text-fg-faint">
                vs {row.counterpart ?? "unknown counterpart"}
              </span>
              {row.agreement !== null ? (
                <span
                  className={`font-mono text-xs font-medium ${row.agreement < 0.8 ? "text-warn" : "text-fg"}`}
                >
                  {row.agreement.toFixed(2)}
                </span>
              ) : (
                <span className="text-xs text-fg-faint">—</span>
              )}
            </div>
            {row.fields.length > 0 && <FieldAgreementBars entries={row.fields} />}
          </div>
        ))}
      </div>

      <MaskMetricsRow record={scorecard} />
      <PendingPeerList uids={pendingPeerUids} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ScorecardView
// ---------------------------------------------------------------------------

export function ScorecardView({ scorecard }: ScorecardViewProps) {
  const record = asRecord(scorecard ?? null);

  if (!record) {
    return <EmptyNote>No scorecard yet — check back once this batch is scored.</EmptyNote>;
  }

  const mode = asString(record.mode);

  if (mode === "gt") {
    return <GtScorecard scorecard={record} />;
  }
  if (mode === "peer") {
    return <PeerScorecard scorecard={record} />;
  }

  // Unknown/missing mode — no signal to branch on, never guess which shape
  // applies.
  return <EmptyNote>Scorecard pending — mode not yet reported.</EmptyNote>;
}

export default ScorecardView;
